import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

export default function ProtectedRoute({ children, requiredType }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);

  useEffect(() => {
    // If user data passed from AuthCallback, skip auth check
    if (location.state?.user) {
      setIsAuthenticated(true);
      return;
    }

    if (!loading) {
      if (!user) {
        setIsAuthenticated(false);
        navigate('/login', { replace: true });
      } else if (user.approval_status === 'pending' && location.pathname !== '/pending-approval') {
        // Redirect pending providers/admins to approval page
        navigate('/pending-approval', { replace: true });
      } else if (user.approval_status === 'rejected' && location.pathname !== '/pending-approval') {
        // Rejected users also see the pending page (with rejection message)
        navigate('/pending-approval', { replace: true });
      } else if (user.user_type === 'owner' || user.is_owner) {
        // Owner can access everything
        setIsAuthenticated(true);
      } else if (user.user_type === 'admin' && user.is_admin) {
        // Approved admin can access admin and customer pages
        setIsAuthenticated(true);
      } else if (requiredType === 'mechanic' && (user.user_type === 'mechanic' || user.user_type === 'roadside_tech')) {
        // Mechanics and roadside techs can access mechanic pages
        setIsAuthenticated(true);
      } else if (requiredType === 'customer' && user.user_type === 'customer') {
        setIsAuthenticated(true);
      } else if (!requiredType) {
        // No specific type required
        setIsAuthenticated(true);
      } else {
        // Redirect to appropriate dashboard
        const dashboardPath = 
          user.user_type === 'owner' || user.is_owner ? '/owner' :
          user.user_type === 'admin' ? '/admin' :
          user.user_type === 'mechanic' || user.user_type === 'roadside_tech' ? '/provider' : 
          '/customer';
        navigate(dashboardPath, { replace: true });
      }
    }
  }, [user, loading, navigate, requiredType, location.state, location.pathname]);

  if (loading || isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
}
